/**
 * Custom error types for better error handling
 */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * Centralized request timeout handling utilities
 */

// Standard timeout for AI requests (30 seconds for good UX)
export const REQUEST_TIMEOUT = 30000;

/**
 * Creates an AbortController with automatic timeout
 * @param timeout - Timeout in milliseconds (default: 30 seconds)
 * @returns Object with abortController and cleanup function
 */
export function createTimeoutController(timeout: number = REQUEST_TIMEOUT) {
  const abortController = new AbortController();

  const timeoutId = setTimeout(() => {
    abortController.abort();
  }, timeout);

  const cleanup = () => {
    clearTimeout(timeoutId);
  };

  return {
    abortController,
    cleanup,
    signal: abortController.signal
  };
}

/**
 * Enhanced error handling for timeout-enabled requests
 * @param error - Error from the request
 * @param customMessage - Optional custom timeout message
 * @returns User-friendly error message
 */
export function handleTimeoutError(error: any, customMessage?: string): string {
  if (error.name === 'AbortError') {
    return customMessage || 'Request timed out (30s). Please try again with a simpler prompt.';
  }

  // Delegate to existing error handler for other types
  return ApiErrorHandler.handleError(error);
}

export class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
  }
}

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class ApiResponseError extends Error {
  public errorCode?: string;

  constructor(message: string, errorCode?: string) {
    super(message);
    this.name = 'ApiResponseError';
    this.errorCode = errorCode;
  }
}

/**
 * Response structure for API calls
 */
export interface ApiResponse<T = string> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

/**
 * Utility functions for error handling
 */
export class ApiErrorHandler {
  /**
   * Parse API response and handle errors consistently
   */
  static parseResponse(response: string): ApiResponse {
    try {
      // Try to parse as JSON first (structured response)
      const parsed = JSON.parse(response);
      if (parsed.success !== undefined) {
        return parsed as ApiResponse;
      }
    } catch {
      // If not JSON, treat as plain text response
    }

    // Check for known error patterns in text responses
    if (this.isErrorResponse(response)) {
      return {
        success: false,
        error: {
          code: this.getErrorCode(response),
          message: this.getErrorMessage(response)
        }
      };
    }

    // Valid response
    return {
      success: true,
      data: response
    };
  }

  /**
   * Check if response contains error indicators
   */
  private static isErrorResponse(response: string): boolean {
    const errorIndicators = [
      'Unable to parse',
      'Error parsing',
      'Error generating',
      'Unable to generate',
      'Invalid response',
      'Request failed',
      'Service unavailable'
    ];

    return errorIndicators.some((indicator) => response.toLowerCase().includes(indicator.toLowerCase()));
  }

  /**
   * Extract error code from response
   */
  private static getErrorCode(response: string): string {
    if (response.includes('Unable to parse')) return 'PARSE_ERROR';
    if (response.includes('Error parsing')) return 'PARSE_ERROR';
    if (response.includes('Error generating')) return 'GENERATION_ERROR';
    if (response.includes('Unable to generate')) return 'GENERATION_ERROR';
    if (response.includes('Invalid response')) return 'INVALID_RESPONSE';
    if (response.includes('Request failed')) return 'REQUEST_FAILED';
    if (response.includes('Service unavailable')) return 'SERVICE_UNAVAILABLE';
    return 'UNKNOWN_ERROR';
  }

  /**
   * Extract user-friendly error message
   */
  private static getErrorMessage(response: string): string {
    const errorCode = this.getErrorCode(response);

    switch (errorCode) {
      case 'PARSE_ERROR':
        return 'The AI service returned an invalid response format. Please try rephrasing your prompt.';
      case 'GENERATION_ERROR':
        return 'Unable to generate a meaningful summary. Please try a different prompt or check if there is sufficient data available.';
      case 'INVALID_RESPONSE':
        return 'The AI service returned an unexpected response. Please try again.';
      case 'REQUEST_FAILED':
        return 'The request failed. Please try again later.';
      case 'SERVICE_UNAVAILABLE':
        return 'The AI service is temporarily unavailable. Please try again later.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Handle different types of errors with appropriate user messages
   */
  static handleError(error: Error): string {
    if (error instanceof NetworkError) {
      return 'Network connection issue. Please check your connection and try again.';
    }

    if (error instanceof TimeoutError) {
      return 'Request timed out. Please try with a simpler prompt or try again later.';
    }

    if (error instanceof ValidationError) {
      return error.message; // Validation errors already have user-friendly messages
    }

    if (error instanceof ApiResponseError) {
      return error.message;
    }

    // Check for specific error patterns in the message
    const message = error.message.toLowerCase();

    if (message.includes('network') || message.includes('connection')) {
      return 'Network connection issue. Please check your connection and try again.';
    }

    if (message.includes('timeout') || message.includes('timed out')) {
      return 'Request timed out. Please try with a simpler prompt or try again later.';
    }

    if (message.includes('no response received')) {
      return 'No response received from the AI service. Please try again.';
    }

    // Log the original error for debugging
    console.error('Unhandled error:', error);

    return 'An unexpected error occurred. Please try again later.';
  }

  /**
   * Validate response content
   */
  static validateResponse(response: string): void {
    if (!response || response.trim().length === 0) {
      throw new ApiResponseError('Empty response received from the AI service.');
    }

    if (response === 'Unable to generate summary at this time.') {
      throw new ApiResponseError('The AI service is unable to generate a summary at this time. Please try again later.');
    }

    // Check minimum content length for meaningful responses
    if (response.trim().length < 10) {
      throw new ApiResponseError('Response too short to be meaningful. Please try a different prompt.');
    }
  }
}
